import * as Sentry from '@sentry/react';
import { ENV_VARS } from '@common/services/env-vars-service';
import { filterGraphqlOutcomeMetric } from '@common/services/sentry-graphql-outcomes';
import { useEffect } from 'react';

/* eslint-disable no-restricted-imports */
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

const dsn = ENV_VARS.SENTRY_DSN;
const appName = ENV_VARS.SENTRY_APP_NAME;
const release = ENV_VARS.APP_VERSION;
const environment = ENV_VARS.SENTRY_ENV;

// This is part of a hacky solution to not send transactions that failed because of errors we purposefully ignored.
// See https://github.com/getsentry/sentry-javascript/issues/13119#issuecomment-2260129565
const spansWithError = new Set();

Sentry.init({
  dsn,
  environment,
  release,
  tracesSampleRate: 0.1,
  replaysSessionSampleRate: 0.0,
  replaysOnErrorSampleRate: environment === 'production' ? 1.0 : 0.0,
  normalizeDepth: 10,
  sendDefaultPii: true,
  beforeSendMetric: filterGraphqlOutcomeMetric,
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: true,
    }),
  ],

  initialScope: {
    tags: {
      ['trusted.app']: appName || 'unknown',
      ['trusted.component']: 'rails-frontend',
    },
  },

  ignoreErrors: [
    // More info in WORK-9497
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications.',
  ],

  beforeSend(event) {
    if (event.type) {
      // Event is not an error
      return event;
    }

    const activeSpan = Sentry.getActiveSpan();
    const rootSpan = activeSpan && Sentry.getRootSpan(activeSpan);
    if (rootSpan) {
      spansWithError.add(Sentry.spanToJSON(rootSpan).span_id);
    }

    return event;
  },

  beforeSendTransaction(transaction) {
    if (environment !== 'production' && environment !== 'staging') return null;

    const trace = transaction?.contexts?.trace;
    if (!trace) {
      return transaction;
    }

    const spanId = trace.span_id;
    if (!spansWithError.has(spanId)) {
      trace.status = 'ok';
    } else {
      // Clean up the set to avoid memory leaks
      spansWithError.delete(spanId);
    }

    return transaction;
  },
});

type User = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
  timeZone: string;
};

type Entity = {
  id: string;
  name: string;
  type: string;
};

type Membership = {
  id: string;
  role: string;
};

export function setSentryMetadata(
  user?: User,
  entity?: Entity,
  membership?: Membership
) {
  if (user) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: `${user.firstName} ${user.lastName}`,
    });
  }

  Sentry.setContext('user', {
    timeZone: user?.timeZone,
    membershipId: membership?.id,
    role: membership?.role,
    entityId: entity?.id,
    entityName: entity?.name,
    entityType: entity?.type,
  });
}

export const captureException = Sentry.captureException;

export const captureMessage = Sentry.captureMessage;

export const ErrorBoundary = Sentry.ErrorBoundary;

export const metrics = Sentry.metrics;
